import { api } from "./api";

/**
 * Web Push on this device (docs/16 decision 17): the service worker's subscription, sent to the API so the notices'
 * first channel can be a notification. The worker is only registered in the built app; in dev there is none.
 */
export type PushState = "unsupported" | "denied" | "available" | "subscribed";

const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!supported()) return undefined;
  try {
    return await navigator.serviceWorker.getRegistration();
  } catch {
    return undefined;
  }
}

/** Where this device stands: no worker or no API, permission refused, subscribed already, or ready to subscribe. */
export async function pushState(): Promise<PushState> {
  const reg = await registration();
  if (!reg) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const sub = await reg.pushManager.getSubscription();
    return sub ? "subscribed" : "available";
  } catch {
    return "available";
  }
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = (base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Asks permission, subscribes with the server's VAPID key and registers the subscription. Throws on refusal or failure. */
export async function subscribePush(publicKey: string): Promise<PushState> {
  const reg = await registration();
  if (!reg) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) throw new Error("subscription_incomplete");
  await api.pushSubscribe({ endpoint: json.endpoint, keys: { p256dh, auth } });
  return "subscribed";
}
